import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LocationProvider } from './contexts/LocationContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import CashOutForm from './pages/CashOutForm';
import CashOutDetail from './pages/CashOutDetail';
import CashOutList from './pages/CashOutList';
import ChemicalDashboard from './pages/ChemicalDashboard';
import ChemicalForm from './pages/ChemicalForm';
import LogChemicalChange from './pages/LogChemicalChange';
import MaintenanceDashboard from './pages/MaintenanceDashboard';
import MaintenanceWorkOrderForm from './pages/MaintenanceWorkOrderForm';
import MaintenanceWorkOrderDetail from './pages/MaintenanceWorkOrderDetail';
import MaintenanceSchedules from './pages/MaintenanceSchedules';
import MaintenanceScheduleForm from './pages/MaintenanceScheduleForm';
import MaintenanceCosts from './pages/MaintenanceCosts';
import MaintenanceSOPs from './pages/MaintenanceSOPs';
import CustomerIssues from './pages/CustomerIssues';
import PLDashboard from './pages/PLDashboard';
import Timesheet from './pages/Timesheet';
import Settings from './pages/Settings';
import SecurityPage from './pages/SecurityPage';
import MessagingConsentPage from './pages/MessagingConsentPage';
import SmsOptInPage from './pages/SmsOptInPage';
import ContactPage from './pages/ContactPage';
import LandingPage from './pages/LandingPage';
import AdminDashboard from './pages/AdminDashboard';
import PublicShell from './components/PublicShell';
import { createCheckoutSession, createPortalSession } from './services/billingService';

// Skip auth in local dev when Firebase isn't configured
const DEV_BYPASS_AUTH = !process.env.REACT_APP_FIREBASE_API_KEY;

function AuthGate({ children }) {
  const { authStatus } = useAuth();
  const location = useLocation();

  if (DEV_BYPASS_AUTH) {
    return children;
  }

  if (authStatus === 'loading') {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (authStatus !== 'approved') {
    // Show the public landing page for unauthenticated visitors at the root —
    // Twilio reviewers and casual visitors shouldn't hit a login wall.
    if (authStatus === 'unauthenticated' && location.pathname === '/') {
      return <PublicShell><LandingPage /></PublicShell>;
    }
    return <LoginPage />;
  }

  return children;
}

function SubscriptionGate({ children }) {
  const { account, user, membership, signOut } = useAuth();
  const [checkoutLoading, setCheckoutLoading] = React.useState(false);

  // Past-due accounts already have a subscription; send them to the portal to
  // fix their card rather than Checkout, which would start a second one.
  const fixPayment = account?.subscription_status === 'past_due' && account?.has_billing_account;

  const handleSubscribe = async () => {
    setCheckoutLoading(true);
    try {
      const { url } = fixPayment ? await createPortalSession() : await createCheckoutSession();
      window.location.href = url;
    } catch (err) {
      alert('Unable to start checkout. Please contact help@carwashcopilot.com');
      setCheckoutLoading(false);
    }
  };

  // Superadmins bypass subscription checks
  if (user?.is_superadmin) return children;
  // If subscription is not active, show expired screen
  if (account && account.subscription_active === false) {
    const isOwnerOrAdmin = membership?.role === 'owner' || membership?.role === 'admin';
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <img src="/logo192.png" alt="Car Wash Copilot" className="w-16 h-16 mx-auto mb-3" />
            <h1 className="text-3xl font-bold text-gray-900">Car Wash Copilot</h1>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {account.subscription_status === 'past_due' ? 'Payment Past Due' : 'Trial Expired'}
            </h2>
            <p className="text-gray-600 mb-6">
              {account.subscription_status === 'past_due'
                ? 'Your last payment failed. Please update your payment method to continue using Car Wash Copilot.'
                : 'Your free trial has ended. Subscribe to continue using Car Wash Copilot.'}
            </p>
            {isOwnerOrAdmin ? (
              <button
                onClick={handleSubscribe}
                disabled={checkoutLoading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 mb-3"
              >
                {checkoutLoading ? 'Redirecting...' : fixPayment ? 'Update Payment Method' : 'Subscribe Now'}
              </button>
            ) : (
              <p className="text-sm text-gray-500 mb-3">
                Please ask your account owner to subscribe to continue using Car Wash Copilot.
              </p>
            )}
            <button
              onClick={signOut}
              className="w-full text-gray-600 py-2 text-sm hover:text-gray-800"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }
  return children;
}

function TrialBanner() {
  const { account, user, membership } = useAuth();
  const [loading, setLoading] = React.useState(false);

  if (user?.is_superadmin) return null;
  if (!account || account.subscription_status !== 'trialing' || account.trial_days_remaining == null) return null;
  const days = account.trial_days_remaining;
  if (days > 7) return null; // Only show when 7 or fewer days left

  const isOwnerOrAdmin = membership?.role === 'owner' || membership?.role === 'admin';

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const { url } = await createCheckoutSession();
      window.location.href = url;
    } catch {
      setLoading(false);
    }
  };

  return (
    <div className={`text-center text-sm py-2 px-4 ${days <= 3 ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
      {days === 0
        ? 'Your free trial ends today.'
        : `Your free trial ends in ${days} day${days !== 1 ? 's' : ''}.`}
      {' '}
      {isOwnerOrAdmin ? (
        <button onClick={handleSubscribe} disabled={loading} className="font-medium underline">
          {loading ? 'Redirecting...' : 'Subscribe now'}
        </button>
      ) : (
        <span>Contact your account owner to subscribe.</span>
      )}
    </div>
  );
}

function SetupRedirect({ children }) {
  const { needsSetup } = useAuth();
  if (needsSetup) return <Navigate to="/settings" replace />;
  return children;
}

function PermissionGate({ page, children }) {
  const { canViewPage, isAdmin, user } = useAuth();
  if (page === 'superadmin' && !user?.is_superadmin) return <Navigate to="/" replace />;
  if (page === 'settings' && !isAdmin) return <Navigate to="/" replace />;
  if (!canViewPage(page)) return <Navigate to="/" replace />;
  return children;
}

function ProtectedApp() {
  return (
    <AuthGate>
      <SubscriptionGate>
      <LocationProvider>
          <TrialBanner />
          <Layout>
            <Routes>
              <Route path="/" element={<SetupRedirect><Dashboard /></SetupRedirect>} />
              <Route path="/pl" element={<SetupRedirect><PermissionGate page="pl"><PLDashboard /></PermissionGate></SetupRedirect>} />
              <Route path="/cash-outs" element={<SetupRedirect><PermissionGate page="pl"><CashOutList /></PermissionGate></SetupRedirect>} />
              <Route path="/cash-out/new" element={<SetupRedirect><PermissionGate page="pl"><CashOutForm /></PermissionGate></SetupRedirect>} />
              <Route path="/cash-out/:id" element={<SetupRedirect><PermissionGate page="pl"><CashOutDetail /></PermissionGate></SetupRedirect>} />
              <Route path="/cash-out/edit/:id" element={<SetupRedirect><PermissionGate page="pl"><CashOutForm /></PermissionGate></SetupRedirect>} />
              <Route path="/inventory" element={<SetupRedirect><PermissionGate page="inventory"><ChemicalDashboard /></PermissionGate></SetupRedirect>} />
              <Route path="/inventory/chemical/new" element={<SetupRedirect><PermissionGate page="inventory"><ChemicalForm /></PermissionGate></SetupRedirect>} />
              <Route path="/inventory/chemical/edit/:id" element={<SetupRedirect><PermissionGate page="inventory"><ChemicalForm /></PermissionGate></SetupRedirect>} />
              <Route path="/inventory/log" element={<SetupRedirect><PermissionGate page="inventory"><LogChemicalChange /></PermissionGate></SetupRedirect>} />
              <Route path="/maintenance" element={<SetupRedirect><MaintenanceDashboard /></SetupRedirect>} />
              <Route path="/maintenance/work-order/new" element={<SetupRedirect><MaintenanceWorkOrderForm /></SetupRedirect>} />
              <Route path="/maintenance/work-order/edit/:id" element={<SetupRedirect><MaintenanceWorkOrderForm /></SetupRedirect>} />
              <Route path="/maintenance/work-order/:id" element={<SetupRedirect><MaintenanceWorkOrderDetail /></SetupRedirect>} />
              <Route path="/maintenance/schedules" element={<SetupRedirect><MaintenanceSchedules /></SetupRedirect>} />
              <Route path="/maintenance/schedule/new" element={<SetupRedirect><MaintenanceScheduleForm /></SetupRedirect>} />
              <Route path="/maintenance/schedule/edit/:id" element={<SetupRedirect><MaintenanceScheduleForm /></SetupRedirect>} />
              <Route path="/maintenance/costs" element={<SetupRedirect><MaintenanceCosts /></SetupRedirect>} />
              <Route path="/maintenance/issues" element={<SetupRedirect><CustomerIssues /></SetupRedirect>} />
              <Route path="/maintenance/sops" element={<SetupRedirect><MaintenanceSOPs /></SetupRedirect>} />
              <Route path="/timesheet" element={<SetupRedirect><Timesheet /></SetupRedirect>} />
              <Route path="/settings" element={<PermissionGate page="settings"><Settings /></PermissionGate>} />
              <Route path="/admin" element={<PermissionGate page="superadmin"><AdminDashboard /></PermissionGate>} />
            </Routes>
          </Layout>
      </LocationProvider>
      </SubscriptionGate>
    </AuthGate>
  );
}

function LoginRoute() {
  const { authStatus } = useAuth();
  if (authStatus === 'loading') {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  if (authStatus === 'approved') return <Navigate to="/" replace />;
  return <LoginPage />;
}

function AppRoutes() {
  return (
    <Router>
      <Routes>
        {/* Public routes — reachable without login, required for Twilio toll-free review */}
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/messaging-consent" element={<PublicShell><MessagingConsentPage /></PublicShell>} />
        <Route path="/sms-opt-in" element={<PublicShell><SmsOptInPage /></PublicShell>} />
        <Route path="/security" element={<PublicShell><SecurityPage /></PublicShell>} />
        <Route path="/contact" element={<PublicShell><ContactPage /></PublicShell>} />

        {/* Everything else goes through auth. AuthGate shows the public
            landing page at `/` for unauthenticated visitors. */}
        <Route path="/*" element={<ProtectedApp />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
